import { hasAnalyticsConsent } from './consent';
import { resolveAppEnv } from './environment';
import { buildPosthogInitConfig, resolvePosthogKey } from './posthogConfig';

/**
 * Camada de analytics/observabilidade sobre o `posthog-js` (LGPD — 2 camadas).
 *
 * ESSENCIAL (legítimo interesse, SEM consentimento, cookieless):
 *   - `captureError()` — evento de erro com o mesmo nome que ia para o Rollbar. Enviado sempre que o SDK carrega.
 *   - `captureException()` — a exceção no Error Tracking (stack trace), agrupada pelo nome do evento.
 *   - Erros não tratados / promises rejeitadas: `capture_exceptions` no init (`posthogConfig.ts`).
 *   - O SDK inicia com `persistence: 'memory'` (sem cookie / sem id persistente) e `autocapture: false`.
 *
 * ANALYTICS (só COM consentimento):
 *   - `captureEvent()` / `capturePageview()` / `identifyUser()` — eventos de funil, pageview, identidade.
 *   - Session replay (`startSessionRecording`) — ligado em `enableAnalytics()` se `VITE_POSTHOG_SESSION_RECORDING=true`.
 *   - No aceite, `enableAnalytics()` promove a persistência para `localStorage+cookie`.
 *
 * - SDK via `import()` dinâmico (chunk separado). Nunca lança. Desligado no Storybook e sem `VITE_POSTHOG_KEY`.
 * - `src/analytics/posthog.ts` é o ÚNICO ponto que toca o SDK.
 */
type PosthogClient = typeof import('posthog-js')['default'];

// Project API Key por ambiente da Vercel (homolog e produção são projetos separados). Sem fallback embutido.
const POSTHOG_KEY = resolvePosthogKey({
  raw: import.meta.env.VITE_POSTHOG_KEY,
  isStorybook: import.meta.env.VITE_STORYBOOK === 'true'
});
const POSTHOG_HOST = String(import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com')
  .trim()
  .replace(/\/+$/, '');
const AMBIENTE = resolveAppEnv({
  appEnv: import.meta.env.VITE_APP_ENV,
  apiUrl: import.meta.env.VITE_ARIDE_API_URL
});
const SESSION_RECORDING_ENABLED = String(import.meta.env.VITE_POSTHOG_SESSION_RECORDING ?? '').trim() === 'true';

let clientPromise: Promise<PosthogClient | null> | null = null;

/** SDK pode carregar (chave presente, browser, fora do Storybook). NÃO exige consentimento. */
export function isPosthogEnabled(): boolean {
  return POSTHOG_KEY.length > 0 && typeof window !== 'undefined' && import.meta.env.VITE_STORYBOOK !== 'true';
}

/** Aplica a camada de analytics (persistência com cookie + session replay opcional). */
function applyAnalyticsConfig(posthog: PosthogClient): void {
  posthog.set_config({ persistence: 'localStorage+cookie' });
  if (SESSION_RECORDING_ENABLED) {
    try {
      posthog.startSessionRecording();
    } catch {
      /* noop */
    }
  }
}

/** Carrega e inicializa o SDK (uma vez), em modo cookieless até haver consentimento. */
function loadClient(): Promise<PosthogClient | null> {
  if (!isPosthogEnabled()) return Promise.resolve(null);
  if (!clientPromise) {
    clientPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        const consented = hasAnalyticsConsent();
        posthog.init(POSTHOG_KEY, buildPosthogInitConfig({ apiHost: POSTHOG_HOST, consented, ambiente: AMBIENTE }));
        if (consented) applyAnalyticsConfig(posthog);
        return posthog;
      })
      .catch(() => null);
  }
  return clientPromise;
}

/** Carrega o SDK para a captura ESSENCIAL de erro. Idempotente e não-bloqueante. */
export function initPosthog(): void {
  if (!isPosthogEnabled()) return;
  void loadClient();
}

/** ESSENCIAL — erro/exceção (legítimo interesse). Enviado sempre, sem depender de consentimento. */
export function captureError(event: string, properties: Record<string, unknown> = {}): void {
  if (!isPosthogEnabled() || !event) return;
  void loadClient()
    .then((ph) => ph?.capture(event, { ...properties, consent_given: hasAnalyticsConsent() }))
    .catch(() => {
      /* best-effort */
    });
}

/**
 * ESSENCIAL — exceção para o Error Tracking (stack trace). Passe um `Error` já sem dados sensíveis
 * (só nome/mensagem/stack); `properties` segue a mesma regra do `captureError`.
 */
export function captureException(error: Error, properties: Record<string, unknown> = {}): void {
  if (!isPosthogEnabled()) return;
  void loadClient()
    .then((ph) => ph?.captureException(error, { ...properties, consent_given: hasAnalyticsConsent() }))
    .catch(() => {
      /* best-effort */
    });
}

/** ANALYTICS — só com consentimento. */
export function captureEvent(event: string, properties: Record<string, unknown> = {}): void {
  if (!isPosthogEnabled() || !event || !hasAnalyticsConsent()) return;
  void loadClient()
    .then((ph) => ph?.capture(event, properties))
    .catch(() => {
      /* best-effort */
    });
}

/** ANALYTICS — identidade (só com consentimento). Não envie PII em `setProperties`. */
export function identifyUser(distinctId: string, setProperties: Record<string, unknown> = {}): void {
  if (!isPosthogEnabled() || !distinctId || !hasAnalyticsConsent()) return;
  void loadClient()
    .then((ph) => ph?.identify(distinctId, setProperties))
    .catch(() => {
      /* noop */
    });
}

/** ANALYTICS — pageview SPA (só com consentimento). */
export function capturePageview(path?: string): void {
  if (!isPosthogEnabled() || !hasAnalyticsConsent()) return;
  void loadClient()
    .then((ph) => ph?.capture('$pageview', path ? { $pathname: path } : undefined))
    .catch(() => {
      /* noop */
    });
}

/** Liga a camada de analytics após o consentimento (cookie/persistência + session replay). */
export function enableAnalytics(): void {
  if (!isPosthogEnabled()) return;
  void loadClient()
    .then((ph) => {
      if (ph) applyAnalyticsConfig(ph);
    })
    .catch(() => {
      /* noop */
    });
}

/** Revoga a camada de analytics (mantém só a captura de erro, cookieless). */
export function disableAnalytics(): void {
  if (!isPosthogEnabled()) return;
  void loadClient()
    .then((ph) => {
      if (!ph) return;
      try {
        ph.stopSessionRecording?.();
      } catch {
        /* noop */
      }
      ph.set_config({ persistence: 'memory' });
    })
    .catch(() => {
      /* noop */
    });
}

export function resetPosthog(): void {
  if (!isPosthogEnabled()) return;
  void loadClient()
    .then((ph) => ph?.reset())
    .catch(() => {
      /* noop */
    });
}
